function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { VIDEO_QUALITIES, qualityNote } from "./video-quality";
import { AssetPicker } from "./sequence-create";

/*
 * New clip page (DS-approved mockup 2026-10-08, PlunkDocs mockups/create-flow-redesign-2026-10-08.html, "Clip" screen).
 * Same visual language as the clip editor (clip-editor.jsx, cx- classes): top bar with the primary action always in view,
 * a quiet grey stage on the left, a 380 px panel on the right. Classes are cn- prefixed and scoped under .cn-view
 * (sass/v2/_clip-new.scss).
 *
 * This file is presentation only. The directed-clip state and the render pipeline stay in ClipForm (clip-workspace.jsx),
 * which renders <ClipNewDirect> when given layout="page"; the clip editor keeps using ClipForm's compact panel layout for
 * Re-direct. Upload mode is <ClipNewUpload>, rendered by ClipWorkspace.renderNew.
 * Nothing here imports clip-workspace.jsx (it imports this file), so the shared clip constants live here.
 */

// 8 s opening shot, then 7 s per directed extension (8 + 7n, up to 148 s); up to eight direction lines, the last repeats.
export const CLIP_LENGTHS = [8, 15, 22, 29, 43, 64, 92, 148];
export const shotsFor = len => 1 + Math.max(0, Math.round((len - 8) / 7));
/** The shortest length that gives every written line its own shot. */
export const lengthForLines = lines => CLIP_LENGTHS.find(l => shotsFor(l) >= Math.max(1, lines)) || CLIP_LENGTHS[CLIP_LENGTHS.length - 1];
export const SIZES = [["16:9", "Wide", "16:9"], ["9:16", "Story", "9:16"], ["1:1", "Square", "1:1"]];
export const sizeLabel = k => {
  const r = SIZES.find(([x]) => x === k) || SIZES[0];
  return `${r[1]} ${r[2]}`;
};
const RATIO = {
  "16:9": 16 / 9,
  "9:16": 9 / 16,
  "1:1": 1
};
const PICK_ONLY = ["product", "image", "render"];

/** Page shell: top bar (back, title, subtitle line, summary, primary action) over the body. */
export function ClipNewShell({
  onBack,
  sub,
  summary,
  action,
  children,
  busy
}) {
  return /*#__PURE__*/React.createElement("div", {
    className: "territory-view wa-view content-view cn-view"
  }, /*#__PURE__*/React.createElement("div", {
    className: "cn"
  }, /*#__PURE__*/React.createElement("div", {
    className: "cn-top"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "cn-iconbtn",
    "aria-label": "Back to Content",
    title: "Back to Content",
    disabled: busy,
    onClick: onBack
  }, /*#__PURE__*/React.createElement("span", {
    className: "material-icons",
    "aria-hidden": "true"
  }, "chevron_left")), /*#__PURE__*/React.createElement("div", {
    className: "cn-title"
  }, /*#__PURE__*/React.createElement("h1", null, "New clip"), /*#__PURE__*/React.createElement("span", null, sub)), /*#__PURE__*/React.createElement("span", {
    className: "cn-sp"
  }), summary ? /*#__PURE__*/React.createElement("span", {
    className: "cn-summary",
    "aria-live": "polite"
  }, summary) : null, action), /*#__PURE__*/React.createElement("div", {
    className: "cn-body"
  }, children)));
}

/** A modal sheet holding an AssetPicker. Escape or the close button dismisses it. */
class PickerSheet extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "closeBtn", /*#__PURE__*/React.createRef());
    _defineProperty(this, "onKey", e => {
      if (e.key === "Escape") this.props.onClose();
    });
  }
  componentDidMount() {
    document.addEventListener("keydown", this.onKey);
    try {
      this.closeBtn.current && this.closeBtn.current.focus();
    } catch (e) {/* no focus */}
  }
  componentWillUnmount() {
    document.removeEventListener("keydown", this.onKey);
  }
  render() {
    const {
      title,
      hint,
      children,
      onClose
    } = this.props;
    return /*#__PURE__*/React.createElement("div", {
      className: "cn-sheet-back",
      onMouseDown: e => {
        if (e.target === e.currentTarget) onClose();
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "cn-sheet",
      role: "dialog",
      "aria-modal": "true",
      "aria-labelledby": "cn-sheet-title"
    }, /*#__PURE__*/React.createElement("div", {
      className: "cn-sheet-head"
    }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("h2", {
      id: "cn-sheet-title"
    }, title), hint ? /*#__PURE__*/React.createElement("p", null, hint) : null), /*#__PURE__*/React.createElement("button", {
      type: "button",
      ref: this.closeBtn,
      className: "cn-iconbtn",
      "aria-label": "Close",
      title: "Close",
      onClick: onClose
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons",
      "aria-hidden": "true"
    }, "close"))), /*#__PURE__*/React.createElement("div", {
      className: "cn-sheet-body"
    }, children)));
  }
}

/**
 * The directed-clip page. Props come from ClipForm (state + actions):
 *   s: { refId, keepIds, assets, assetsLoaded, aspect, shots, title, busy, progress, credits, longer, quality }
 *   imageUrl, lines (filled lines), length (effective seconds), autoLength, n (shots rendered), maxLines, maxKeep, placeholders,
 *   assetUrl(id), set(patch), setShot(i, v), addShot(), removeShot(i), setRef(id), addKeep(id), removeKeep(id), make(),
 *   onBack(), onUploadMode()
 */
export class ClipNewDirect extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      sheet: ""
    });
    // "" | "image" | "keep"
    _defineProperty(this, "shotRefs", []);
    _defineProperty(this, "addShot", () => {
      const k = this.props.s.shots.length;
      this.props.addShot();
      setTimeout(() => {
        const el = this.shotRefs[k];
        if (el) el.focus();
      }, 0);
    });
  }
  pickerList() {
    const s = this.props.s;
    return s.assetsLoaded ? s.assets : undefined;
  }
  renderStage() {
    const p = this.props;
    const s = p.s;
    const busy = s.busy;
    if (!p.imageUrl) {
      if (s.refId && !s.assetsLoaded) return /*#__PURE__*/React.createElement("div", {
        className: "cn-stage"
      }, /*#__PURE__*/React.createElement("div", {
        className: "cn-loading",
        role: "status"
      }, /*#__PURE__*/React.createElement("span", {
        className: "material-icons cn-spin",
        "aria-hidden": "true"
      }, "autorenew"), "Loading the image\u2026"));
      return /*#__PURE__*/React.createElement("div", {
        className: "cn-stage top"
      }, /*#__PURE__*/React.createElement("div", {
        className: "cn-pickcard"
      }, /*#__PURE__*/React.createElement("div", {
        className: "cn-pickhead"
      }, /*#__PURE__*/React.createElement("span", {
        className: "cn-pickicon material-icons",
        "aria-hidden": "true"
      }, "add_photo_alternate"), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("h2", null, "Pick the image the clip starts from"), /*#__PURE__*/React.createElement("p", null, "It becomes the first frame. Pick one from Assets, or upload a new one."))), /*#__PURE__*/React.createElement(AssetPicker, {
        single: true,
        only: PICK_ONLY,
        accept: "image",
        assets: this.pickerList(),
        picks: [],
        disabled: busy,
        onAssets: list => p.set({
          assets: list,
          assetsLoaded: true
        }),
        onChange: ids => {
          if (ids[0]) p.setRef(ids[0]);
        }
      })));
    }
    const ratio = RATIO[s.aspect] || RATIO["16:9"];
    const slots = [];
    for (let i = 0; i < p.maxKeep; i++) slots.push(s.keepIds[i] || null);
    const firstEmpty = slots.indexOf(null);
    return /*#__PURE__*/React.createElement("div", {
      className: "cn-stage"
    }, /*#__PURE__*/React.createElement("div", {
      className: "cn-col"
    }, /*#__PURE__*/React.createElement("div", {
      className: "cn-frame",
      style: {
        aspectRatio: `${ratio}`,
        width: `min(100%, calc(${ratio.toFixed(4)} * min(52vh, 420px)))`
      }
    }, /*#__PURE__*/React.createElement("img", {
      src: p.imageUrl,
      alt: "The starting image"
    }), /*#__PURE__*/React.createElement("span", {
      className: "cn-badge"
    }, "First frame \xB7 ", sizeLabel(s.aspect)), busy ? /*#__PURE__*/React.createElement("span", {
      className: "cn-busy",
      role: "status"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons cn-spin",
      "aria-hidden": "true"
    }, "autorenew"), s.progress ? `Rendering · ${s.progress}` : "Rendering") : /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "cn-change",
      onClick: () => this.setState({
        sheet: "image"
      })
    }, "Change image")), /*#__PURE__*/React.createElement("div", {
      className: "cn-keep"
    }, /*#__PURE__*/React.createElement("div", {
      className: "cn-keep-head"
    }, /*#__PURE__*/React.createElement("b", {
      id: "cn-keep-label"
    }, "Keep it looking the same"), /*#__PURE__*/React.createElement("span", null, "optional \xB7 up to ", p.maxKeep, " more angles of the same thing")), /*#__PURE__*/React.createElement("div", {
      className: "cn-slots",
      role: "group",
      "aria-labelledby": "cn-keep-label"
    }, slots.map((id, i) => id ? /*#__PURE__*/React.createElement("div", {
      key: id,
      className: "cn-slot"
    }, p.assetUrl(id) ? /*#__PURE__*/React.createElement("img", {
      src: p.assetUrl(id),
      alt: `Extra angle ${i + 1}`
    }) : /*#__PURE__*/React.createElement("span", {
      className: "material-icons",
      "aria-hidden": "true"
    }, "image"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "cn-x",
      "aria-label": `Remove extra angle ${i + 1}`,
      title: "Remove",
      disabled: busy,
      onClick: () => p.removeKeep(id)
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons",
      "aria-hidden": "true"
    }, "close"))) : /*#__PURE__*/React.createElement("button", {
      key: "add" + i,
      type: "button",
      className: "cn-slot add" + (i === firstEmpty ? "" : " later"),
      disabled: busy,
      onClick: () => this.setState({
        sheet: "keep"
      })
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons",
      "aria-hidden": "true"
    }, "add"), "Add")))), s.credits ? /*#__PURE__*/React.createElement("div", {
      className: "cn-notice",
      role: "status"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons",
      "aria-hidden": "true"
    }, "info"), /*#__PURE__*/React.createElement("span", null, "Not enough credits for this clip."), /*#__PURE__*/React.createElement("span", {
      className: "cn-sp"
    }), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "cn-btn sm",
      onClick: () => window.open(s.credits.buyUrl, "_blank", "noopener")
    }, "Buy credits")) : null));
  }
  renderPanel() {
    const p = this.props;
    const s = p.s;
    const busy = s.busy;
    const n = p.n;
    const filled = p.lines;
    const repeats = n - Math.max(1, filled);
    const longerOptions = CLIP_LENGTHS.filter(l => l > p.autoLength);
    const squareOff = p.length > 8;
    return /*#__PURE__*/React.createElement("div", {
      className: "cn-side"
    }, /*#__PURE__*/React.createElement("div", {
      className: "cn-group"
    }, /*#__PURE__*/React.createElement("label", {
      className: "cn-h",
      htmlFor: "cn-shot-0"
    }, "What should happen?"), /*#__PURE__*/React.createElement("p", {
      className: "cn-help"
    }, "One line per shot. Describe the camera and the light. Your image stays the subject."), /*#__PURE__*/React.createElement("ol", {
      className: "cn-shots"
    }, s.shots.map((line, i) => /*#__PURE__*/React.createElement("li", {
      key: i,
      className: "cn-shot" + (i === 0 ? " first" : "")
    }, /*#__PURE__*/React.createElement("span", {
      className: "cn-n",
      "aria-hidden": "true"
    }, i + 1), /*#__PURE__*/React.createElement("textarea", {
      id: `cn-shot-${i}`,
      ref: el => {
        this.shotRefs[i] = el;
      },
      rows: 2,
      value: line,
      disabled: busy,
      "aria-label": i === 0 ? undefined : `Shot ${i + 1}`,
      placeholder: p.placeholders[i % p.placeholders.length],
      onChange: e => p.setShot(i, e.target.value)
    }), s.shots.length > 1 ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "cn-rm",
      "aria-label": `Remove shot ${i + 1}`,
      title: "Remove this shot",
      disabled: busy,
      onClick: () => p.removeShot(i)
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons",
      "aria-hidden": "true"
    }, "close")) : null))), s.shots.length < p.maxLines ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "cn-add",
      disabled: busy,
      onClick: this.addShot
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons",
      "aria-hidden": "true"
    }, "add"), "Add a shot") : /*#__PURE__*/React.createElement("p", {
      className: "cn-help"
    }, "That is the most lines one clip takes."), /*#__PURE__*/React.createElement("p", {
      className: "cn-help"
    }, n, " shot", n === 1 ? "" : "s", " \xB7 about ", p.length, " seconds. Each shot adds about 7 seconds.", repeats > 0 && filled > 0 ? ` Your last line is used again for the last ${repeats === 1 ? "shot" : `${repeats} shots`}.` : ""), longerOptions.length ? /*#__PURE__*/React.createElement("label", {
      className: "cn-longer"
    }, /*#__PURE__*/React.createElement("span", null, "Make it longer"), /*#__PURE__*/React.createElement("select", {
      value: s.longer > p.autoLength ? String(s.longer) : "",
      disabled: busy,
      onChange: e => p.set({
        longer: Number(e.target.value) || 0
      })
    }, /*#__PURE__*/React.createElement("option", {
      value: ""
    }, "As written \xB7 ", p.autoLength, " s"), longerOptions.map(l => /*#__PURE__*/React.createElement("option", {
      key: l,
      value: String(l)
    }, l, " s \xB7 ", shotsFor(l), " shots")))) : null), /*#__PURE__*/React.createElement("div", {
      className: "cn-group"
    }, /*#__PURE__*/React.createElement("span", {
      className: "cn-h",
      id: "cn-size-label"
    }, "Size"), /*#__PURE__*/React.createElement("div", {
      className: "cn-seg",
      role: "group",
      "aria-labelledby": "cn-size-label"
    }, SIZES.map(([k, name, r]) => {
      const off = k === "1:1" && squareOff;
      return /*#__PURE__*/React.createElement("button", {
        key: k,
        type: "button",
        className: s.aspect === k ? "on" : "",
        "aria-pressed": s.aspect === k,
        disabled: busy || off,
        title: off ? "Square works for one-shot clips of 8 seconds" : "",
        onClick: () => p.set({
          aspect: k
        })
      }, name, " ", r);
    })), /*#__PURE__*/React.createElement("p", {
      className: "cn-help"
    }, squareOff ? "Square works for one-shot clips of 8 seconds. " : "", "You can make the other sizes later in the clip editor.")), /*#__PURE__*/React.createElement("div", {
      className: "cn-group"
    }, /*#__PURE__*/React.createElement("span", {
      className: "cn-h",
      id: "cn-quality-label"
    }, "Quality"), /*#__PURE__*/React.createElement("div", {
      className: "cn-seg",
      role: "group",
      "aria-labelledby": "cn-quality-label"
    }, VIDEO_QUALITIES.map(([k, name]) => /*#__PURE__*/React.createElement("button", {
      key: k,
      type: "button",
      className: s.quality === k ? "on" : "",
      "aria-pressed": s.quality === k,
      disabled: busy,
      onClick: () => p.setQuality ? p.setQuality(k) : p.set({
        quality: k
      })
    }, name))), /*#__PURE__*/React.createElement("p", {
      className: "cn-help"
    }, qualityNote(s.quality), ".")), /*#__PURE__*/React.createElement("div", {
      className: "cn-group"
    }, /*#__PURE__*/React.createElement("label", {
      className: "cn-h",
      htmlFor: "cn-name"
    }, "Name ", /*#__PURE__*/React.createElement("i", null, "optional")), /*#__PURE__*/React.createElement("input", {
      id: "cn-name",
      className: "cn-input",
      value: s.title,
      disabled: busy,
      placeholder: "Named from the first shot if empty",
      onChange: e => p.set({
        title: e.target.value
      })
    })), /*#__PURE__*/React.createElement("span", {
      className: "cn-sp"
    }), /*#__PURE__*/React.createElement("p", {
      className: "cn-help foot"
    }, "You can leave this page while it's made; the clip appears in Assets when it's done. It opens in the editor, where you can trim, cut, caption and extend it."));
  }
  renderSheet() {
    const p = this.props;
    const s = p.s;
    const sheet = this.state.sheet;
    if (!sheet) return null;
    const close = () => this.setState({
      sheet: ""
    });
    if (sheet === "image") {
      return /*#__PURE__*/React.createElement(PickerSheet, {
        title: "Change the starting image",
        hint: "Pick another image from Assets, or upload one.",
        onClose: close
      }, /*#__PURE__*/React.createElement(AssetPicker, {
        single: true,
        only: PICK_ONLY,
        accept: "image",
        assets: this.pickerList(),
        picks: s.refId ? [s.refId] : [],
        onAssets: list => p.set({
          assets: list,
          assetsLoaded: true
        }),
        onChange: ids => {
          if (ids[0]) p.setRef(ids[0]);
          close();
        }
      }));
    }
    return /*#__PURE__*/React.createElement(PickerSheet, {
      title: "Add another angle",
      hint: "Another photo of the same thing helps it look the same in every shot.",
      onClose: close
    }, /*#__PURE__*/React.createElement(AssetPicker, {
      single: true,
      only: PICK_ONLY,
      accept: "image",
      assets: this.pickerList(),
      picks: [],
      exclude: [s.refId, ...s.keepIds].filter(Boolean),
      onAssets: list => p.set({
        assets: list,
        assetsLoaded: true
      }),
      onChange: ids => {
        if (ids[0]) p.addKeep(ids[0]);
        close();
      }
    }));
  }
  render() {
    const p = this.props;
    const s = p.s;
    const busy = s.busy;
    const n = p.n;
    const ready = !!p.imageUrl;
    const summary = `${n} shot${n === 1 ? "" : "s"} · about ${p.length} s · a few minutes per shot`;
    const sub = /*#__PURE__*/React.createElement(React.Fragment, null, "Bring an image to life \xB7 ", /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "cn-link",
      disabled: busy,
      onClick: p.onUploadMode
    }, "Upload a video instead"));
    const action = /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "cn-primary",
      disabled: busy || !ready,
      onClick: p.make,
      "aria-live": "polite",
      title: ready ? "" : "Pick a starting image first"
    }, busy ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
      className: "material-icons cn-spin",
      "aria-hidden": "true"
    }, "autorenew"), s.progress || "Rendering…") : "Make the clip");
    return /*#__PURE__*/React.createElement(ClipNewShell, {
      onBack: p.onBack,
      sub: sub,
      summary: summary,
      action: action,
      busy: busy
    }, /*#__PURE__*/React.createElement("div", {
      className: "cn-left"
    }, this.renderStage()), this.renderPanel(), this.renderSheet());
  }
}

/** Upload mode: the stage is one large drop zone; the side panel says what happens next. */
export class ClipNewUpload extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      over: false
    });
    _defineProperty(this, "drop", e => {
      e.preventDefault();
      this.setState({
        over: false
      });
      if (this.props.busy) return;
      const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (f) this.props.onFile(f);
    });
  }
  render() {
    const {
      busy,
      pct,
      onBack,
      onDirectMode,
      onFile
    } = this.props;
    const sub = /*#__PURE__*/React.createElement(React.Fragment, null, "Upload a video \xB7 ", /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "cn-link",
      disabled: busy,
      onClick: onDirectMode
    }, "Bring an image to life instead"));
    const action = /*#__PURE__*/React.createElement("label", {
      className: "cn-primary" + (busy ? " disabled" : "")
    }, /*#__PURE__*/React.createElement("input", {
      type: "file",
      className: "cn-sr",
      accept: "video/mp4,video/quicktime,video/webm",
      disabled: busy,
      onChange: e => {
        onFile(e.target.files && e.target.files[0]);
        e.target.value = "";
      }
    }), busy ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
      className: "material-icons cn-spin",
      "aria-hidden": "true"
    }, "autorenew"), "Uploading \xB7 ", pct, "%") : "Choose a video");
    return /*#__PURE__*/React.createElement(ClipNewShell, {
      onBack: onBack,
      sub: sub,
      action: action,
      busy: busy
    }, /*#__PURE__*/React.createElement("div", {
      className: "cn-left"
    }, /*#__PURE__*/React.createElement("div", {
      className: "cn-stage"
    }, /*#__PURE__*/React.createElement("label", {
      className: "cn-drop" + (this.state.over ? " over" : "") + (busy ? " busy" : ""),
      onDragOver: e => {
        e.preventDefault();
        if (!this.state.over) this.setState({
          over: true
        });
      },
      onDragLeave: () => this.setState({
        over: false
      }),
      onDrop: this.drop
    }, /*#__PURE__*/React.createElement("input", {
      type: "file",
      className: "cn-sr",
      accept: "video/mp4,video/quicktime,video/webm",
      disabled: busy,
      onChange: e => {
        onFile(e.target.files && e.target.files[0]);
        e.target.value = "";
      }
    }), /*#__PURE__*/React.createElement("span", {
      className: "cn-dropicon material-icons",
      "aria-hidden": "true"
    }, busy ? "cloud_upload" : "movie"), /*#__PURE__*/React.createElement("b", null, busy ? `Uploading · ${pct}%` : "Choose a video or drop it here"), /*#__PURE__*/React.createElement("span", null, "MP4, MOV or WEBM"), busy ? /*#__PURE__*/React.createElement("span", {
      className: "cn-bar",
      role: "progressbar",
      "aria-valuemin": 0,
      "aria-valuemax": 100,
      "aria-valuenow": pct,
      "aria-label": "Upload progress"
    }, /*#__PURE__*/React.createElement("i", {
      style: {
        width: `${Math.max(2, pct)}%`
      }
    })) : null))), /*#__PURE__*/React.createElement("div", {
      className: "cn-side"
    }, /*#__PURE__*/React.createElement("div", {
      className: "cn-group"
    }, /*#__PURE__*/React.createElement("span", {
      className: "cn-h"
    }, "Upload a video"), /*#__PURE__*/React.createElement("p", {
      className: "cn-help"
    }, "Footage, a product video or a screen recording. When the upload finishes it opens in the clip editor.")), /*#__PURE__*/React.createElement("div", {
      className: "cn-group"
    }, /*#__PURE__*/React.createElement("span", {
      className: "cn-h sm"
    }, "In the editor you can"), /*#__PURE__*/React.createElement("ul", {
      className: "cn-list"
    }, /*#__PURE__*/React.createElement("li", null, /*#__PURE__*/React.createElement("span", {
      className: "material-icons",
      "aria-hidden": "true"
    }, "content_cut"), "Trim it and keep only the parts you want"), /*#__PURE__*/React.createElement("li", null, /*#__PURE__*/React.createElement("span", {
      className: "material-icons",
      "aria-hidden": "true"
    }, "subtitles"), "Add captions"), /*#__PURE__*/React.createElement("li", null, /*#__PURE__*/React.createElement("span", {
      className: "material-icons",
      "aria-hidden": "true"
    }, "crop"), "Make other sizes: wide, tall or square"), /*#__PURE__*/React.createElement("li", null, /*#__PURE__*/React.createElement("span", {
      className: "material-icons",
      "aria-hidden": "true"
    }, "history"), "Go back to any earlier version"))), /*#__PURE__*/React.createElement("span", {
      className: "cn-sp"
    }), /*#__PURE__*/React.createElement("p", {
      className: "cn-help foot"
    }, "Keep this page open until the upload finishes.")));
  }
}